import moment from "moment";
import React from "react";
import Avatar from "../../../../library/Avatar";
import NotAssignedCoordinatorIcon from "../../../../library/NotAssignedCoordinatorIcon";
import IconButton, { StyledActionGroup, StyledIconLink } from "../../../../library/IconButton";
import { TABLE_DATE_FORMAT, TABLE_DATE_TIME_FORMAT } from "../../../../../utils/Constants";
import PermissionsGate from "../../../../../utils/permissionGate";
import UnassignedModulesStatus from "./UnassignedModulesStatus";
import {
    StyledCard,
    StyledTop,
    StyledHeaderRow,
    StyledName,
    StyledBadges,
    StyledBadge,
    StyledCoordinatorRow,
    StyledCoordinatorInfo,
    StyledCoordinatorName,
    StyledCoordinatorRole,
    StyledCounters,
    StyledCounter,
    StyledCounterLabel,
    StyledCounterValue,
    StyledNoteGroup,
    StyledNotes,
    StyledNoteAttribution,
    StyledDatesRow,
    StyledDateDivider,
    StyledFooter,
    StyledOpenLink,
    StyledOpenButton,
} from "./VersionCard.styles";

const COUNTERS = [
    { key: "programmes", icon: "books", label: "Programmes" },
    { key: "modules", icon: "book", label: "Modules" },
    { key: "students", icon: "users", label: "Students" },
    { key: "graduates", icon: "graduation-cap", label: "Graduates" },
];

/**
 * Locked/Unlocked and Active/Inactive status badges for a version card header.
 */
function VersionCardBadges({ isLocked, isActive }) {
    return (
        <StyledBadges>
            <StyledBadge $variant={isLocked ? "locked" : "unlocked"}>
                <i className={`fal fa-${isLocked ? "lock" : "unlock"}`} aria-hidden="true" />
                {isLocked ? "Locked" : "Unlocked"}
            </StyledBadge>

            <StyledBadge $variant={isActive ? "active" : "inactive"}>
                <i className={`fas fa-${isActive ? "circle-check" : "times-circle"}`} aria-hidden="true" />
                {isActive ? "Active" : "Inactive"}
            </StyledBadge>
        </StyledBadges>
    );
}

/**
 * Avatar/initials + name + role for a version card's Qualification Co-ordinator, or a
 * "Not Assigned" state (the summary strip's unassigned icon) when none is set. The card has
 * no "Assign" affordance, unlike the summary strip.
 */
function VersionCardCoordinator({ coordinator }) {
    const name = coordinator?.name || "";

    if (!name) {
        return (
            <StyledCoordinatorRow>
                <NotAssignedCoordinatorIcon />

                <StyledCoordinatorInfo>
                    <StyledCoordinatorName>Not Assigned</StyledCoordinatorName>
                    <StyledCoordinatorRole>Qualification Co-ordinator</StyledCoordinatorRole>
                </StyledCoordinatorInfo>
            </StyledCoordinatorRow>
        );
    }

    return (
        <StyledCoordinatorRow>
            <Avatar name={name} image={coordinator?.avatar} size={28} />

            <StyledCoordinatorInfo>
                <StyledCoordinatorName>{name}</StyledCoordinatorName>
                <StyledCoordinatorRole>Qualification Co-ordinator</StyledCoordinatorRole>
            </StyledCoordinatorInfo>
        </StyledCoordinatorRow>
    );
}

/**
 * Programmes/Modules/Students/Graduates counter row for a version card.
 */
function VersionCardCounters({ totals }) {
    return (
        <StyledCounters>
            {COUNTERS.map((counter) => (
                <StyledCounter key={counter.key}>
                    <StyledCounterLabel>
                        <i className={`fal fa-${counter.icon}`} aria-hidden="true" />
                        {counter.label}
                    </StyledCounterLabel>
                    <StyledCounterValue>{totals[counter.key] ?? 0}</StyledCounterValue>
                </StyledCounter>
            ))}
        </StyledCounters>
    );
}

/**
 * Notes text + its audit attribution ("by {author} · {DD MMM YY, HH:mm}"). Renders nothing
 * when there's no note - not blank note space, not a placeholder "by ..." line. The
 * attribution line itself only renders when both `author` and `updatedAt` were actually
 * resolved from a real audit-trail record; a note with no matching Notes-change audit event
 * still shows its text, just without a fabricated "by ..." line underneath.
 */
function VersionCardNote({ notes, author, updatedAt }) {
    if (!notes) {
        return null;
    }

    return (
        <StyledNoteGroup>
            <StyledNotes>{notes}</StyledNotes>

            {author && updatedAt && (
                <StyledNoteAttribution>
                    by {author} · {moment(updatedAt).format(TABLE_DATE_TIME_FORMAT)}
                </StyledNoteAttribution>
            )}
        </StyledNoteGroup>
    );
}

/**
 * "Modified"/"Created" date row for a version card.
 */
function VersionCardDates({ createdAt, modifiedAt }) {
    return (
        <StyledDatesRow>
            <span>
                <i className="fal fa-history" aria-hidden="true" />
                Modified {modifiedAt ? moment(modifiedAt).format(TABLE_DATE_FORMAT) : "—"}
            </span>

            <StyledDateDivider aria-hidden="true" />

            <span>
                Created {createdAt ? moment(createdAt).format(TABLE_DATE_FORMAT) : "—"}
            </span>
        </StyledDatesRow>
    );
}

/**
 * Open/Duplicate/Delete actions for a version card.
 */
function VersionCardActions({ id, programmeId, name, isActive, onDelete }) {
    return (
        <StyledFooter>
            <StyledOpenLink
                to={`/program-management-center/programmes/versions/details?versionId=${id}&programmeId=${programmeId}`}
            >
                <StyledOpenButton variant="primary" size="lg">
                    <i className="fal fa-folder-open" aria-hidden="true" />
                    Open
                </StyledOpenButton>
            </StyledOpenLink>

            <StyledActionGroup>
                <PermissionsGate
                    RenderError={() => <IconButton icon="copy" variant="outline" disabled ariaLabel="Duplicate" />}
                    scopes={["pmcversionadd"]}
                >
                    <StyledIconLink
                        to={`/program-management-center/programmes/versions/clone?versionId=${id}&programmeId=${programmeId}`}
                        title="Duplicate"
                    >
                        <IconButton icon="copy" variant="outline" ariaLabel="Duplicate" />
                    </StyledIconLink>
                </PermissionsGate>

                <PermissionsGate
                    RenderError={() => <IconButton icon="trash-alt" variant="destructive" disabled ariaLabel="Delete" />}
                    scopes={["pmcversiondelete"]}
                >
                    <IconButton
                        icon="trash-alt"
                        variant="destructive"
                        ariaLabel="Delete"
                        title="Delete"
                        onClick={() => onDelete?.(id, name, isActive)}
                    />
                </PermissionsGate>
            </StyledActionGroup>
        </StyledFooter>
    );
}

/**
 * One PMC version card on the Versions list: status/lock badges, Qualification
 * Co-ordinator, key totals, optional notes (with audit attribution), Unassigned
 * Modules status, modified/created dates, and the Open/Duplicate/Delete actions.
 */
function VersionCard({
    id,
    programmeId,
    name,
    isActive,
    isLocked,
    coordinator,
    totals = {},
    unassignedModulesCount = 0,
    notes,
    noteAuthor,
    noteUpdatedAt,
    createdAt,
    modifiedAt,
    onDelete,
}) {
    return (
        <StyledCard>
            <StyledTop>
                <StyledHeaderRow>
                    <StyledName title={name}>{name}</StyledName>
                    <VersionCardBadges isLocked={isLocked} isActive={isActive} />
                </StyledHeaderRow>

                <VersionCardCoordinator coordinator={coordinator} />
                <VersionCardCounters totals={totals} />
                <VersionCardNote notes={notes} author={noteAuthor} updatedAt={noteUpdatedAt} />
                <UnassignedModulesStatus count={unassignedModulesCount} />
                <VersionCardDates createdAt={createdAt} modifiedAt={modifiedAt} />
            </StyledTop>

            <VersionCardActions id={id} programmeId={programmeId} name={name} isActive={isActive} onDelete={onDelete} />
        </StyledCard>
    );
}

export default VersionCard;
